import { useForm, Controller, SubmitHandler } from "react-hook-form";
import React, { useEffect, useState } from "react";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import { useTranslation } from "next-i18next";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import { answerQuestions } from "@/hooks/useRequest/useSurveyRequest";
import { IdentityTypes } from "@/interfaces/enums";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import ValidateError from "@/containers/common/ValidateError";
import ContentContainer from "@/containers/common/ContentContainer";
import Input from "@/components/Input";

interface DocumentNumberProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	orcIdentityNo?: string;
	ocrIdentity?: number;
	onNextClick: () => void;
}

interface FormState {
	documentNumber: string;
}

function DocumentNumber(props: DocumentNumberProps) {
	const { t } = useTranslation("survey");
	const [loading, setLoading] = useState(false);
	const [identity, setIdentity] = useState<IdentityTypes>();
	const { control, handleSubmit, formState, setValue } = useForm<FormState>({
		mode: "onSubmit",
		defaultValues: { documentNumber: "" },
	});
	const { isValid, errors } = formState;

	useEffect(() => {
		setValue("documentNumber", props.answerFieldToValueMap.get("documentNumber") || "", {
			shouldValidate: props.answerFieldToValueMap.get("documentNumber") ? true : false,
		});
	}, []);

	useEffect(() => {
		const documentType = props.answerFieldToValueMap.get("documentType");
		if (!documentType) return;

		setIdentity(Number(documentType));
	}, []);

	const renderTextByIdentity = () => {
		if (identity === IdentityTypes.ID_CARD) {
			return {
				label: t("id-number"),
				rule: t("please-enter-the-correct-id-number"),
				text: t("id-card"),
			};
		}
		if (identity === IdentityTypes.ALIEN_ID_CARD) {
			return {
				label: t("alien-id-card-placeholder"),
				rule: t("please-enter-the-correct-alien-id-number"),
				text: t("alien-id-card"),
			};
		}
		return {
			label: t("passport-number"),
			rule: t("please-enter-the-correct-passport-number"),
			text: t("passport"),
		};
	};

	const onSubmit: SubmitHandler<FormState> = async (data) => {
		try {
			const documentNumberQuestionId = props.questionFieldToIdMap.get("documentNumber");
			if (!documentNumberQuestionId) {
				throw Error("missing documentNumber questionId");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: data.documentNumber,
					questionId: documentNumberQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_IDNUMBER_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_IDNUMBER_VIEW);
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			footer={
				<SurveyFooter
					loading={loading}
					disabled={!isValid}
					onNextBtnClick={handleSubmit(onSubmit)}
					onPrivacyClick={() => log2sensors(SensorsEventIds.AO_IDNUMBER_PRAVICY)}
					onSecureClick={() => log2sensors(SensorsEventIds.AO_IDNUMBER_SECURITY)}
				/>
			}
		>
			<SurveyTitle
				title={t("what-is-your-passport-id-number")}
				subtitle={t("the-identity-number-should-match-the-identity-proof-you-upload")}
			/>
			<div className="h-12" />
			<Controller
				name="documentNumber"
				control={control}
				defaultValue=""
				rules={{
					required: renderTextByIdentity().rule,
					maxLength: {
						value: 256,
						message: t("maximum-word-limit-exceeded"),
					},
				}}
				render={({ field }) => (
					<Input
						{...field}
						placeholder={renderTextByIdentity().label}
						onClick={() => {
							if (identity === IdentityTypes.ID_CARD) {
								log2sensors(SensorsEventIds.AO_IDNUMBER_INPIDNUMBER);
							} else {
								log2sensors(SensorsEventIds.AO_IDNUMBER_INPPASSPORTNUMBER);
							}
						}}
					/>
				)}
			/>

			<ValidateError error={errors.documentNumber} />
		</ContentContainer>
	);
}

export default DocumentNumber;
